import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { AppCheckbox } from "../../../../../../../shared/components/AppCheckbox";
import { TCaseField, TFeasibilityStatus } from "../../../../../types";
import { AppLabel } from "../../../../../../../shared/components/AppLabel";
import { AppIconButton } from "../../../../../../../shared/components/AppIconButton";
import { FeasibilityAdornment } from "../FeasibilityAdornment";
import { SparksIcon } from "../../../../../../../shared/icons/SparksIcon";
import { TScoringKeys } from "../../../../../../../services/api/instances/casesApi/types";
import { useAiScoringById } from "../../../../../hook/use-ai-scoring-by-id";
import { AppInfoTooltip } from "../../../../../../../common/AppInfoTooltip";
import { useAppDispatch } from "../../../../../../../services/redux/hooks/use-dispatch";
import { getAiTipAction } from "../../../../../redux/actions";
import { CaseTipBlock } from "../CaseTipBlock";
import { useAppSelector } from "../../../../../../../services/redux/hooks/use-selector";
import {
  selectAiTips,
  selectCaseEntityMode,
  selectCasesLoading,
  selectCaseValue,
  selectIsTipLoading,
} from "../../../../../redux/selectors";
import { AppLoading } from "../../../../../../../shared/components/AppLoading";
import { AppNewInput } from "../../../../../../../shared/components/AppNewInput";
import { ClickAwayListener } from "@mui/material";
import { clearAiTip } from "../../../../../redux/slice";
import { FEASIBILITY_STATUS } from "../../../../../constants";
import { getDisplayValue, getFeasibilityPrefix } from "../../../../../helpers";
import { LongArrowIcon } from "../../../../../../../shared/icons/LongArrowIcon";
import styles from "./styles.module.css";

interface Props {
  field: TCaseField;
  handleChangeField: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string,
    path: string,
  ) => void;
  handleClickTip: (field: string, value: string) => void;
  className?: string;
}

export const CaseField = ({
  field,
  handleChangeField,
  handleClickTip,
  className,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { aiScoring } = useAiScoringById();
  const caseValue = useAppSelector(selectCaseValue);
  const isCaseLoading = useAppSelector(selectCasesLoading);
  const tips = useAppSelector(selectAiTips);
  const isTipLoading = useAppSelector(selectIsTipLoading);
  const entityMode = useAppSelector(selectCaseEntityMode);
  const [isAdornmentOpen, setIsAdornmentOpen] = useState<boolean>(false);
  const [feasibilityStatus, setFeasibilityStatus] =
    useState<TFeasibilityStatus>("unknown");

  useEffect(() => {
    if (typeof field.value === "string" && field.label === "feasibility") {
      setFeasibilityStatus(getFeasibilityPrefix(field.value));
    }
  }, [field.value]);

  const cls = classNames(
    styles["input"],
    field.needSave && styles["field-changed"],
    aiScoring[field.label as TScoringKeys]?.status === "wrong" &&
      styles["input-error"],
    aiScoring[field.label as TScoringKeys]?.status === "needs_clarification" &&
      styles["input-need-work"],
    aiScoring[field.label as TScoringKeys]?.status === "ok" &&
      styles["input-valid"],
  );

  const handleAdornmentItemClick = useCallback(
    (status: TFeasibilityStatus) => {
      if (typeof field.value === "string") {
        const existingPrefix = FEASIBILITY_STATUS.find((s) =>
          (field.value as string).startsWith(`${s} | `),
        );
        const cleanValue = existingPrefix
          ? field.value.slice(`${existingPrefix} | `.length)
          : field.value;

        const finalValue =
          status === "unknown" ? cleanValue : `${status} | ${cleanValue}`;

        handleChangeField(finalValue, field.path);
      }
      setFeasibilityStatus(status as TFeasibilityStatus);
    },
    [field.value, field.path, feasibilityStatus],
  );

  const handleGetTip = () => {
    dispatch(getAiTipAction(field.label));
  };

  const handleClickTipField = (value: string) => {
    handleClickTip(field.path, value);
    handleCloseTip();
  };

  const handleCloseTip = () => {
    dispatch(clearAiTip());
  };

  if (typeof field.value === "boolean") {
    return (
      <AppCheckbox
        checked={field.value}
        onChange={(e) => {
          handleChangeField(e, field.path);
        }}
      >
        {t(`cases.case_${field.label as string}_label`)}
      </AppCheckbox>
    );
  }

  return (
    <>
      {field.label !== "skills" && (
        <div key={field.path} className={styles["segment"]}>
          {field.label !== "expert_scoring" && (
            <div className={styles["label-container"]}>
              <div className={styles["label-info"]}>
                {field.label !== "expert_scoring" && (
                  <AppLabel className={styles["case-label"]}>
                    {t(`cases.case_${field.label as string}_label`)}
                  </AppLabel>
                )}
                {field.tooltip && (
                  <AppInfoTooltip
                    title={
                      aiScoring[field.label as TScoringKeys]?.reason ??
                      field.tooltip
                    }
                    place="top"
                    className={styles["case-tooltip"]}
                  />
                )}
                {field.label !== "feasibility" &&
                  field.label !== "name" &&
                  field.label !== "expert_scoring" &&
                  field.value !== "" &&
                  isCaseLoading && (
                    <AppLoading settingSize={16} className={styles["loader"]} />
                  )}
              </div>
              {isTipLoading[field.label] ? (
                <AppLoading settingSize={16} className={styles["loader"]} />
              ) : (
                <>
                  {field.label !== "feasibility" &&
                    field.label !== "name" &&
                    field.label !== "task_description" &&
                    field.label !== "expert_scoring" &&
                    entityMode === "case" && (
                      <AppIconButton
                        onClick={handleGetTip}
                        className={styles["wand-btn"]}
                        disabled={
                          caseValue?.case_object?.case_info
                            ?.task_description === ""
                        }
                      >
                        <SparksIcon />
                      </AppIconButton>
                    )}
                </>
              )}
            </div>
          )}
          <div className={styles["input-container"]}>
            {field.label === "expert_scoring" ? (
              <div className={styles["expert-scoring-content"]}>
                {field.value ? field.value : "Здесь будет оценка эксперта"}
              </div>
            ) : (
              <>
                <div className={styles["input-wrapper"]}>
                  <AppNewInput
                    value={getDisplayValue(field)}
                    onChange={(e) => {
                      handleChangeField(e, field.path);
                    }}
                    minRows={field.inputRows}
                    multiline
                    className={cls}
                    endAdornment={
                      field.label === "feasibility" && (
                        <FeasibilityAdornment
                          feasibilityStatus={feasibilityStatus}
                          handleClick={() =>
                            setIsAdornmentOpen(!isAdornmentOpen)
                          }
                          isMenuOpen={isAdornmentOpen}
                          onItemClick={(status) =>
                            handleAdornmentItemClick(
                              status as TFeasibilityStatus,
                            )
                          }
                        />
                      )
                    }
                  />
                  {(field.label === "input_artifact" ||
                    field.label === "output_artifact") && (
                    <LongArrowIcon className={styles["input-arrow"]} />
                  )}
                </div>
                {tips?.variants && field.label === tips.field && (
                  <ClickAwayListener onClickAway={handleCloseTip}>
                    <CaseTipBlock
                      tips={tips?.variants}
                      handleClickTip={handleClickTipField}
                    />
                  </ClickAwayListener>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
